import { HttpErrorResponse, HttpStatusCode } from '@angular/common/http';
import {
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  Renderer2,
  ViewChild,
} from '@angular/core';
import {
  FormGroup,
  FormBuilder,
  FormControl,
  Validators,
  AbstractControl,
} from '@angular/forms';
import { BreakpointObserver } from '@angular/cdk/layout';
import { LoginService } from '@services';
import { getFormControl, httpErrorStatus } from '@utils';
import { FormStatus } from '@enums';
import { ButtonCustomSizeConfig } from 'nanaykuna-ui-kit/loader';
import { catchError, EMPTY, Subject, takeUntil } from 'rxjs';
import {
  ACCESS_BUTTON_SIZE_DESKTOP,
  ACCESS_BUTTON_SIZE_MOBILE,
  ACCESS_DESKTOP_QUERY,
} from '@components/access-layout/access-button-size';

type PasswordField = 'newPassword' | 'confirmPassword';

const MISMATCH_MESSAGE = 'Las contraseñas no coinciden.';
const POLICY_REJECTED_MESSAGE =
  'Tu contraseña no cumple los requisitos. Revísalos e inténtalo de nuevo.';

@Component({
  standalone: false,
  selector: 'digicem-password-form',
  templateUrl: './password-form.component.html',
  host: { class: 'flex flex-col flex-1' },
})
export class PasswordFormComponent implements OnInit, AfterViewInit, OnDestroy {
  @Input() public token: string = '';
  // `heading`, not `title`: see AccessHeaderComponent (native tooltip).
  @Input() public heading: string = 'Crea tu nueva contraseña';
  @Input() public subTitle: string =
    'Ingresa tu nueva contraseña y repítela para confirmarla.';
  @Input() public buttonText: string = 'Restablecer contraseña';
  @Input() public titleSuccess: string = 'Contraseña restablecida';
  @Input() public messageSuccess: string =
    'Ya puedes iniciar sesión con tu nueva contraseña y seguir usando UNACEM 360.';
  @Input() public buttonSuccessText: string = 'Iniciar sesión';
  /** Shown for any failure that is not an expired link (401) or a rejected policy (422). */
  @Input() public genericErrorMessage: string =
    'No se pudo restablecer tu contraseña. Inténtalo de nuevo.';
  @Input() public isDialog: boolean = false;
  @Output() public onChangeStatusForm: EventEmitter<FormStatus> =
    new EventEmitter();

  protected newPasswordForm!: FormGroup;
  protected loading = false;
  protected error = '';
  protected formSubmitted = false;
  protected buttonSize: ButtonCustomSizeConfig = ACCESS_BUTTON_SIZE_MOBILE;

  protected hasUpperCase = false;
  protected hasLowerCase = false;
  protected hasNumber = false;
  protected hasSpecialChar = false;
  protected hasMinLength = false;

  @ViewChild('confirmPassword') confirmPasswordRef!: ElementRef;

  private destroy$ = new Subject<void>();

  constructor(
    private formBuilder: FormBuilder,
    private loginService: LoginService,
    private renderer: Renderer2,
    private breakpointObserver: BreakpointObserver,
  ) {
    this.setNewPasswordForm();
  }

  ngOnInit(): void {
    this.breakpointObserver
      .observe(ACCESS_DESKTOP_QUERY)
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.buttonSize = state.matches
          ? ACCESS_BUTTON_SIZE_DESKTOP
          : ACCESS_BUTTON_SIZE_MOBILE;
      });
  }

  ngAfterViewInit(): void {
    if (!this.confirmPasswordRef) {
      return;
    }
    this.renderer.listen(
      this.confirmPasswordRef.nativeElement,
      'paste',
      /* istanbul ignore next */
      event => {
        event.preventDefault();
      },
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private setNewPasswordForm(): void {
    this.newPasswordForm = this.formBuilder.group({
      newPassword: new FormControl<string>(
        '',
        Validators.compose([
          Validators.required,
          Validators.minLength(8),
          Validators.pattern(/[A-Z]/), // Al menos una mayúscula
          Validators.pattern(/[a-z]/), // Al menos una minúscula
          Validators.pattern(/[0-9]/), // Al menos un número
          Validators.pattern(/[\W_]/), // Al menos un carácter especial
        ]),
      ),
      confirmPassword: new FormControl<string>('', [
        Validators.required,
        this.matchPassword.bind(this),
      ]),
    });

    this.newPasswordForm
      .get('newPassword')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.newPasswordForm.get('confirmPassword')?.updateValueAndValidity();
      });
  }

  private matchPassword(
    control: AbstractControl,
  ): { [key: string]: boolean } | null {
    if (!this.newPasswordForm) return null;
    return control.value === this.newPasswordForm.get('newPassword')?.value
      ? null
      : { passwordMismatch: true };
  }

  protected onInputed(event: CustomEvent, formControl: PasswordField): void {
    const control = this.newPasswordForm.controls[formControl];
    control.setValue(event.detail);
    control.markAsDirty();
    this.error = '';
    if (formControl === 'newPassword') {
      this.onChangePassword(event.detail);
    }
  }

  public onChangePassword(password: string): void {
    this.hasUpperCase = /[A-Z]/.test(password);
    this.hasLowerCase = /[a-z]/.test(password);
    this.hasNumber = /[0-9]/.test(password);
    this.hasSpecialChar = /[\W_]/.test(password);
    this.hasMinLength = password.length >= 8;
  }

  /** The new-password field turns red while a typed value misses a rule. */
  protected get newPasswordInvalid(): boolean {
    const control = this.newPasswordForm.controls['newPassword'];
    return control.dirty && !!control.value && control.invalid;
  }

  protected get confirmError(): string {
    const control = this.newPasswordForm.controls['confirmPassword'];
    return control.dirty &&
      !!control.value &&
      control.hasError('passwordMismatch')
      ? MISMATCH_MESSAGE
      : '';
  }

  protected onSubmit(): void {
    if (this.loading || this.newPasswordForm.invalid) {
      return;
    }
    this.loading = true;
    this.error = '';
    this.loginService
      .changePassword({
        ...this.newPasswordForm.value,
        token: this.token,
      })
      .pipe(
        catchError((error: HttpErrorResponse) => {
          this.loading = false;
          this.handleSubmitError(error);
          return EMPTY;
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.loading = false;
        this.onChangeStatusForm.emit(FormStatus.SENT);
        this.formSubmitted = true;
      });
  }

  /**
   * Decided by status only: every 401 (expired, invalid or wrong-purpose
   * token) needs a new link, and the backend `detail` — which can carry the
   * email or RUC — is never rendered.
   */
  private handleSubmitError(error: HttpErrorResponse): void {
    const status = httpErrorStatus(error);
    if (status === HttpStatusCode.Unauthorized) {
      this.onChangeStatusForm.emit(FormStatus.EXPIRED);
      return;
    }
    this.error =
      status === HttpStatusCode.UnprocessableEntity
        ? POLICY_REJECTED_MESSAGE
        : this.genericErrorMessage;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  protected control(controlName: string): AbstractControl<any, any> {
    return getFormControl(controlName, this.newPasswordForm);
  }

  protected finishForm(): void {
    this.onChangeStatusForm.emit(FormStatus.FINISHED);
  }

  protected closeDialog(): void {
    this.onChangeStatusForm.emit(FormStatus.CLOSED);
  }
}
